import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add,
  Close,
  Delete,
  Edit,
  MenuBook,
} from "@mui/icons-material";
import {
  createGuidancePost,
  deleteGuidancePost,
  getGuidancePosts,
  updateGuidancePost,
} from "../../context/Religious";

const CATEGORY_SUGGESTIONS = [
  "Marriage",
  "Choosing a Spouse",
  "Family Involvement",
  "Making the Decision",
  "Adab",
  "Faith",
  "General",
];

const emptyForm = {
  title: "",
  category: "",
  body: "",
  author: "MJMarry Team",
  isActive: true,
};

export default function GuidanceComponent() {
  const theme = useTheme();

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Editor dialog
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
  const [deleting, setDeleting] = useState(false);

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const fetchPosts = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getGuidancePosts();
      setPosts(data.posts || []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load guidance posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setEditorOpen(true);
  };

  const openEdit = (post) => {
    setEditingId(post._id);
    setForm({
      title: post.title || "",
      category: post.category || "",
      body: post.body || "",
      author: post.author || "MJMarry Team",
      isActive: post.isActive !== false,
    });
    setEditorOpen(true);
  };

  const handleChange = (field) => (e) => {
    const value = field === "isActive" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.category.trim() || !form.body.trim()) {
      showSnackbar("Title, category and body are all required.", "error");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        title: form.title.trim(),
        category: form.category.trim(),
        body: form.body,
        author: form.author.trim() || "MJMarry Team",
        isActive: form.isActive,
      };
      if (editingId) {
        const { post } = await updateGuidancePost(editingId, payload);
        setPosts((prev) => prev.map((p) => (p._id === editingId ? post : p)));
        showSnackbar("Guidance post updated.");
      } else {
        const { post } = await createGuidancePost(payload);
        setPosts((prev) => [post, ...prev]);
        showSnackbar("Guidance post published.");
      }
      setEditorOpen(false);
    } catch (err) {
      showSnackbar(err.response?.data?.message || err.message || "Save failed.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await deleteGuidancePost(deleteDialog.id);
      setPosts((prev) => prev.filter((p) => p._id !== deleteDialog.id));
      showSnackbar("Guidance post deleted.");
      setDeleteDialog({ open: false, id: null });
    } catch (err) {
      showSnackbar(err.response?.data?.message || err.message || "Delete failed.", "error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        py: 4,
        px: { xs: 2, sm: 3 },
        backgroundColor: theme.palette.background.default,
        minHeight: "100vh",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" color={theme.palette.text.primary}>
            Guidance
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Islamic Corner "Guidance" tab in the app. Only active posts are shown to users.
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
          New Post
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", height: "50vh", alignItems: "center" }}>
          <CircularProgress color="secondary" />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      ) : posts.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: "center", backgroundColor: theme.palette.background.paper }}>
          <MenuBook sx={{ fontSize: 56, color: theme.palette.text.secondary, opacity: 0.4, mb: 1 }} />
          <Typography variant="h6" color="text.secondary">
            No guidance posts yet
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Use "New Post" to publish your first piece of guidance.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {posts.map((post) => (
            <Grid item xs={12} md={6} lg={4} key={post._id}>
              <Card
                sx={{
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: 3,
                  borderRadius: 2,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  opacity: post.isActive === false ? 0.6 : 1,
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
                    <Chip label={post.category} size="small" color="primary" variant="outlined" />
                    {post.isActive === false && (
                      <Chip label="Hidden" size="small" color="default" />
                    )}
                  </Box>
                  <Typography variant="h6" fontWeight="bold" gutterBottom color={theme.palette.text.primary}>
                    {post.title}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      display: "-webkit-box",
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {post.body}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" mt={1.5}>
                    {post.author}
                    {post.postedAt
                      ? ` · ${new Date(post.postedAt).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })}`
                      : ""}
                  </Typography>
                </CardContent>
                <Divider />
                <Box sx={{ display: "flex", justifyContent: "flex-end", px: 1, py: 0.5, backgroundColor: theme.palette.action.hover }}>
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => openEdit(post)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, id: post._id })}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Editor dialog */}
      <Dialog open={editorOpen} onClose={() => !saving && setEditorOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {editingId ? "Edit Guidance Post" : "New Guidance Post"}
          <IconButton onClick={() => setEditorOpen(false)} disabled={saving}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <TextField
            label="Title"
            fullWidth
            margin="normal"
            value={form.title}
            onChange={handleChange("title")}
          />
          <TextField
            label="Category"
            fullWidth
            margin="normal"
            select
            value={form.category}
            onChange={handleChange("category")}
            helperText="Pick a category (shown as a chip in the app)."
          >
            {CATEGORY_SUGGESTIONS.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Body"
            fullWidth
            margin="normal"
            multiline
            minRows={8}
            value={form.body}
            onChange={handleChange("body")}
            helperText="Line breaks are preserved in the app."
          />
          <TextField
            label="Author"
            fullWidth
            margin="normal"
            value={form.author}
            onChange={handleChange("author")}
          />
          <FormControlLabel
            control={<Switch checked={form.isActive} onChange={handleChange("isActive")} />}
            label="Visible to users"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? "Saving…" : editingId ? "Save Changes" : "Publish"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={deleteDialog.open} onClose={() => !deleting && setDeleteDialog({ open: false, id: null })}>
        <DialogTitle>Delete Guidance Post</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this post? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
          >
            {deleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
